import { useEffect, useMemo, useState } from "react";

/**
 * Replay the animated code simulation used in the hero.
 * Translation: Replay the animated code simulation used in the hero.
 *
 * @param {Array<Object>} segments - Code segment collection.
 * @param {boolean} prefersReducedMotion - Accessibility preference.
 * @returns {Array<Object>} Typed code segments ready for rendering.
 */
const useCodeSimulation = (segments, prefersReducedMotion) => {
  const [segmentIndex, setSegmentIndex] = useState(0);
  const [characterIndex, setCharacterIndex] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion || !segments.length) {
      return undefined;
    }

    if (segmentIndex >= segments.length) {
      const timeoutId = window.setTimeout(() => {
        setSegmentIndex(0);
        setCharacterIndex(0);
      }, 8000);

      return () => window.clearTimeout(timeoutId);
    }

    const currentSegment = segments[segmentIndex];
    const currentDelay = Math.floor(Math.random() * 45) + 35;

    const timeoutId = window.setTimeout(() => {
      if (characterIndex < currentSegment.text.length) {
        setCharacterIndex((currentValue) => currentValue + 1);
      } else {
        setSegmentIndex((currentValue) => currentValue + 1);
        setCharacterIndex(0);
      }
    }, currentDelay);

    return () => window.clearTimeout(timeoutId);
  }, [characterIndex, prefersReducedMotion, segmentIndex, segments]);

  const renderedSegments = useMemo(() => {
    if (prefersReducedMotion) {
      return segments;
    }

    return segments
      .map((segment, index) => {
        if (index < segmentIndex) {
          return segment;
        }

        if (index === segmentIndex) {
          return {
            ...segment,
            text: segment.text.slice(0, characterIndex),
          };
        }

        return {
          ...segment,
          text: "",
        };
      })
      .filter((segment) => segment.text.length > 0);
  }, [characterIndex, prefersReducedMotion, segmentIndex, segments]);

  return renderedSegments;
};

export default useCodeSimulation;
